import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Instance } from 'mobx-state-tree';

import * as stores from '../../stores';
import { IQueryableRouteComponentProps } from 'easit--routing';
import { goBack, goHome, redirect } from '../../../../routing/routing';
import { TicketType, TicketCalculationsContainer, TicketDetailStore, TicketDetailSourceType, MtsTicketStore, evalFooterVisibility, OfferType, CancelError } from 'eTip--common';

import { BetDetailCashout } from 'common--design-library';
import { SimpleBarWrapper as SimpleBar, TicketDetail } from 'desktop--design-library';
import { FooterContainer } from '../footer/footerContainer';

import { translation, translationForMts } from 'easit--localization';
import * as routing from 'easit--routing';
import { paths, pushToEventDetail, redirectToLiveEventDetailFromPrematch } from '../../routing';

export interface ITicketDetailQueryParams {
    ticketId?: string;
    publicTicketId?: string;
    ticketNumber?: string;
}

export interface ITicketDetailContainerProps extends IQueryableRouteComponentProps<ITicketDetailQueryParams, {}> {
    applicationStore?: IAppStoreWithTicketDetail;
}

export interface IAppStoreWithTicketDetail {
    ticketDetail: Instance<typeof TicketDetailStore>;
    ticketWrapper: Instance<typeof MtsTicketStore>;
    canShowAddPublicLinkToTicketToChat: boolean;
}

export abstract class TicketDetailContainerBase extends React.Component<ITicketDetailContainerProps> {

    protected mounted = false;
    private liveEventStatusUpdateTimeout: number | null;

    constructor(props: ITicketDetailContainerProps) {
        super(props);
    }

    abstract loadDataAsync(): Promise<void>;

    componentWillMount() {
        this.mounted = true;
        let promise = this.loadDataAsync();

        if (this.detailStore.canShowLiveEventStatusInTicketDetail) {
            promise.then(() => {
                this.updateLiveEventStatusAsync(); // update immediately as getTicketDetail may have an older score
            });
        }
    }

    componentWillUnmount() {
        this.mounted = false;
        this.stopLiveEventStatusUpdates();
    }

    get detailStore() {
        return this.props.applicationStore.ticketDetail;
    }

    get wrapperStore() {
        return this.props.applicationStore.ticketWrapper;
    }

    abstract canShowLinkToAddToChat: boolean;

    abstract addPublicLinkToChat(hash: string, isLegacy: boolean): void;

    abstract goToPrematchEvent(id: number): void;

    abstract goToLiveEvent(id: number): void;

    private translation = translationForMts();

    protected stopLiveEventStatusUpdates() {
        if (this.liveEventStatusUpdateTimeout) {
            window.clearTimeout(this.liveEventStatusUpdateTimeout);
            this.liveEventStatusUpdateTimeout = null;
        }
    }

    protected updateLiveEventStatusAsync() {
        return this.detailStore.updateLiveEventStatusAsync()
            .then((resp: boolean) => {
                if (resp) {
                    this.planNextLiveEventStatusUpdates();
                }
            })
            .catch((reason) => {
                if (!(reason instanceof CancelError)) {
                    this.planNextLiveEventStatusUpdates();
                }
            });
    }

    protected planNextLiveEventStatusUpdates() {
        if (!this.mounted) {
            return;
        }
        this.liveEventStatusUpdateTimeout = window.setTimeout(() => {
            this.updateLiveEventStatusAsync();
        }, this.detailStore.liveEventStatusRefreshTimeMs);
    }

    render() {

        return <SimpleBar className="h-100" forceVisible={true} scrollToTop>
            <TicketDetail
                canShowNewFeatureScore={this.detailStore.canShowNewFeatureScore}
                canShowFinalScoreInfo={this.detailStore.canShowFinalScoreInfo}
                shouldGoHomeOnBack={this.detailStore.sourceType == TicketDetailSourceType.PublicTicket}
                dateCreated={this.detailStore.placementDate}
                possiblePayout={this.detailStore.potentialWin}
                winPaid={this.detailStore.winToPay}
                finalWin={this.detailStore.finalWin}
                offerType={this.detailStore.offerType}
                finalRate={this.detailStore.rate}
                ticketTypeAsText={this.detailStore.ticketType === TicketType.System ? this.translation.ticket_TicketTypeSystem : this.translation.ticket_TicketTypeSimple}
                stake={this.detailStore.stake}
                stakePoints={this.detailStore.stakePoints}
                handlingFee={this.detailStore.handingFee}
                handlingFeePoints={this.detailStore.handingFeePoints}
                ticketState={this.detailStore.ticketState}
                evalState={this.detailStore.evalState}
                number={this.detailStore.number}
                customerNickName={this.detailStore.customerNickName}
                prizePaidDate={this.detailStore.prizePaidDate}

                dateCancelationLimit={this.detailStore.dateCancelationLimit}
                canShowCancelButton={this.detailStore.canShowCancelButton}
                cancelState={this.detailStore.cancelState}
                cancelMessage={this.detailStore.cancelMessage}
                onCancel={this.detailStore.cancelCouponAsync}
                onReuseOdds={() => { this.detailStore.reuseOddsAsync(this.wrapperStore.assignSelectionsToTicketAsync, false); }}
                reuseOddsState={this.detailStore.reuseOddsState}
                reuseOddsMessage={this.detailStore.reuseOddsMessage}
                canShowReuseOddsButton={this.detailStore.canShowReuseOddsButton}
                isOTB={this.wrapperStore.isOTB}

                isNotLegacy={this.detailStore.isNotLegacy}
                legacyContent={this.detailStore.legacyContent}

                state={this.detailStore.state}
                error={this.detailStore.error}

                selections={this.detailStore.selections}
                isSystem={this.detailStore.ticketType === TicketType.System}

                publicTicketLink={this.detailStore.publicUrl}

                productFee={this.detailStore.productFee}
                productName={this.detailStore.productName}
                oddsRateAdvantage={this.detailStore.oddsRateAdvantage}
                rateAdvantageFactor={this.detailStore.rateAdvantageFactor}

                title={translation().ticket_detail}
                goBack={() => { if (this.detailStore.backUrl) { redirect(this.detailStore.backUrl, false, true); return; }; this.detailStore.sourceType == TicketDetailSourceType.PublicTicket ? goHome() /* not final, also may come from my today tickets */ : goBack() }}
                refresh={() => { this.loadDataAsync(); }}
                insertPublicLinkToChat={
                    this.canShowLinkToAddToChat ?
                        () => {
                            this.addPublicLinkToChat(this.detailStore.publicUrlHash, !this.detailStore.isNotLegacy);
                        } : null
                }

                onTransfer={(_x) => { }}

                combinations={
                    this.detailStore.ticketType === TicketType.System &&
                    <TicketCalculationsContainer
                        calculations={this.detailStore.calculations}
                        selectionCount={this.detailStore.selectionsWithoutBankerCnt}
                    />
                }

                cashOut={
                    this.detailStore.sourceType == TicketDetailSourceType.MyTicket && <BetDetailCashout
                        cashOut={this.detailStore.cashOut}
                        cashOutErrors={this.detailStore.cashOutErrors}
                        cashOutState={this.detailStore.cashOutState}
                        cashoutAcceptedChangeType={this.detailStore.cashoutAcceptedChangeType}
                        decreaseAgreement={this.detailStore.decreaseAgreement}
                        decreaseAgreementPercents={this.detailStore.decreaseAggreementPercents}
                        decreaseValue={this.detailStore.decreaseValue}
                        isCashoutAvailable={this.detailStore.isCashoutEnabled}
                        onCashOut={this.detailStore.submitCashOutAsync}
                        onCashoutAcceptedChangeType={this.detailStore.toggleCashoutAcceptedChangeType}
                        onDecreaseAgreementChange={this.detailStore.decreaseAgreementToggle}
                        onDecreaseValueChange={this.detailStore.decreaseValueChange}
                        isMobile={false}
                        showLabel={true}
                        isCountDownEnabled={this.detailStore.isCountdownEnabled}
                        numberToCountDown={this.detailStore.visualDelay}
                    />
                }

                onGoToEvent={(id: number, offerType: OfferType, hasLiveEventStatus: boolean) => { this.detailStore.isNotLegacy ? (offerType == OfferType.Live || hasLiveEventStatus ? this.goToLiveEvent(id) : this.goToPrematchEvent(id)) : null }}
            />
            <FooterContainer canShow={evalFooterVisibility(this.detailStore.state)} />
        </SimpleBar>
    }
}

export abstract class TicketDetailMyTicketBaseContainer extends TicketDetailContainerBase {
    private timerId: number;

    get ticketId(): string {
        return this.props.match.params.ticketId;
    }

    private planNextRefresh(ticketId: string) {
        if (this.timerId) {
            window.clearTimeout(this.timerId);
        }
        this.timerId = window.setTimeout(() => {
            this.detailStore.loadMyTicketAsync(ticketId, true)  // it does not stop updateScore, in store it sets last live main score values from updateScore!
                .finally(() => {
                    this.planNextRefresh(ticketId);
                });
        }, 30000);
    }

    checkAndRedirectToTiposLegacy(ticketId: string): boolean {
        if (__IsMTSTipos__) { //TODOHMTS nasty
            if (this.detailStore.isLegacyTicketId(ticketId)) {
                routing.redirect(paths().getLegacyTicketDetailForTipos(ticketId, this.detailStore.backUrl), false, true);
                return true;
            }
        }
        return false;
    }

    componentWillReceiveProps(nextProps: any) {
        if (this.props.match.params.ticketId != nextProps.match.params.ticketId) {
            if (__IsMTSTipos__ && this.checkAndRedirectToTiposLegacy(nextProps.match.params.ticketId)) {
                return;
            }

            this.detailStore.loadMyTicketAsync(nextProps.match.params.ticketId).finally(() => {
                this.planNextRefresh(nextProps.match.params.ticketId);
            });
            return;
        }
    }

    componentWillMount() {
        if (__IsMTSTipos__ && this.checkAndRedirectToTiposLegacy(this.ticketId)) {
            return;
        }
        super.componentWillMount();
    }

    componentWillUnmount() {
        window.clearTimeout(this.timerId);
        if (super.componentWillUnmount) {
            super.componentWillUnmount();
        }
    }

    loadDataAsync(): Promise<void> {
        return this.detailStore.loadMyTicketAsync(this.ticketId).finally(() => {
            this.planNextRefresh(this.ticketId);
        });
    }
}

export abstract class TicketDetailPublicTicketBaseContainer extends TicketDetailContainerBase {

    get publicTicketId(): string {
        return this.props.match.params.publicTicketId;
    }

    componentWillReceiveProps(nextProps: any) {
        if (this.props.match.params.publicTicketId != nextProps.match.params.publicTicketId) {
            var promise = this.detailStore.loadPublicTicketAsync(nextProps.match.params.publicTicketId);
            if (__IsMTSTipos__) {
                promise.then(() => {
                    this.checkIsLegacyForTipos(nextProps.match.params.publicTicketId);
                });
            }
            return;
        }
    }

    loadDataAsync(): Promise<void> {
        var promise = this.detailStore.loadPublicTicketAsync(this.publicTicketId);
        if (!__IsMTSTipos__) {
            return promise;
        }
        return new Promise((f, r) => {
            promise.then(() => {
                this.checkIsLegacyForTipos(this.publicTicketId);
                f();
            })
                .catch(() => {
                    r();
                });
        });
    }

    checkIsLegacyForTipos(publicTicketId: string) {
        if (__IsMTSTipos__) {
            if (!this.detailStore.isNotLegacy) {
                routing.redirect(paths().getLegacyTicketDetailPublic(publicTicketId));
            }
        }
    }
}

export abstract class TicketDetailLegacyPublicTicketBaseContainer extends TicketDetailContainerBase {

    get publicTicketId(): string {
        return this.props.match.params.publicTicketId;
    }

    componentWillReceiveProps(nextProps: any) {
        if (this.props.match.params.publicTicketId != nextProps.match.params.publicTicketId) {
            this.detailStore.loadLegacyPublicTicketAsync(nextProps.match.params.publicTicketId);
            return;
        }
    }

    loadDataAsync(): Promise<void> {
        return this.detailStore.loadLegacyPublicTicketAsync(this.publicTicketId);
    }


}

export abstract class TicketDetailTicketByNumberBaseContainer extends TicketDetailContainerBase {

    get ticketNumber(): string {
        return this.props.match.params.ticketNumber;
    }

    componentWillReceiveProps(nextProps: any) {
        if (this.props.match.params.ticketNumber != nextProps.match.params.ticketNumber) {
            this.detailStore.loadFindTicketByNumberAsync(nextProps.match.params.ticketNumber);
            return;
        }
    }

    loadDataAsync(): Promise<void> {
        return this.detailStore.loadFindTicketByNumberAsync(this.ticketNumber);
    }
}

@inject(stores.ApplicationStoreKey)
@observer
export class TicketDetailMyTicketContainer extends TicketDetailMyTicketBaseContainer {
    canShowLinkToAddToChat: boolean = this.props.applicationStore.canShowAddPublicLinkToTicketToChat;
    addPublicLinkToChat(hash: string, isLegacy: boolean): void {
        document.location.href = `/live/?couponToChat${isLegacy ? "l" : ""}=${hash}`;
    }

    goToPrematchEvent(id: number): void {
        pushToEventDetail({ eventKey: id.toString() });
    }

    goToLiveEvent(id: number): void {
        redirectToLiveEventDetailFromPrematch({ eventKey: id.toString() });
    }
}

@inject(stores.ApplicationStoreKey)
@observer
export class TicketDetailPublicTicketContainer extends TicketDetailPublicTicketBaseContainer {
    canShowLinkToAddToChat: boolean = this.props.applicationStore.canShowAddPublicLinkToTicketToChat;
    addPublicLinkToChat(hash: string): void {
        document.location.href = `/live/?couponToChat=${hash}`;
    }

    goToPrematchEvent(id: number): void {
        pushToEventDetail({ eventKey: id.toString() });
    }

    goToLiveEvent(id: number): void {
        redirectToLiveEventDetailFromPrematch({ eventKey: id.toString() });
    }
}

@inject(stores.ApplicationStoreKey)
@observer
export class TicketDetailLegacyPublicTicketContainer extends TicketDetailLegacyPublicTicketBaseContainer {
    canShowLinkToAddToChat: boolean = this.props.applicationStore.canShowAddPublicLinkToTicketToChat;

    addPublicLinkToChat(hash: string): void {
        document.location.href = `/live/?couponToChatl=${hash}`;
    }

    goToPrematchEvent(id: number): void {
        pushToEventDetail({ eventKey: id.toString() });
    }

    goToLiveEvent(id: number): void {
        redirectToLiveEventDetailFromPrematch({ eventKey: id.toString() });
    }
}

@inject(stores.ApplicationStoreKey)
@observer
export class TicketDetailTicketByNumberContainer extends TicketDetailTicketByNumberBaseContainer {
    canShowLinkToAddToChat: boolean = this.props.applicationStore.canShowAddPublicLinkToTicketToChat;
    addPublicLinkToChat(hash: string, isLegacy: boolean): void {
        document.location.href = `/live/?couponToChat${isLegacy ? "l" : ""}=${hash}`;
    }

    goToPrematchEvent(id: number): void {
        pushToEventDetail({ eventKey: id.toString() });
    }

    goToLiveEvent(id: number): void {
        redirectToLiveEventDetailFromPrematch({ eventKey: id.toString() });
    }
}
